import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { useUser } from '../../context/UserContext';

export default function TeamMessageModal() {
  const { user } = useUser();
  const [message, setMessage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show to non-admin users
    if (!user || user.role === 'admin') {
      return;
    }

    checkForTeamMessage();

    // Listen for team message updates
    const handleMessageUpdate = () => {
      checkForTeamMessage();
    };

    window.addEventListener('team-message-updated', handleMessageUpdate);
    return () => window.removeEventListener('team-message-updated', handleMessageUpdate);
  }, [user]);

  const checkForTeamMessage = () => {
    const teamMessage = JSON.parse(localStorage.getItem('team_message') || 'null');
    
    if (!teamMessage || !teamMessage.isActive) {
      setIsVisible(false);
      return;
    }

    // Check if message has expired
    if (teamMessage.expiryDate) {
      const expiryDate = new Date(teamMessage.expiryDate);
      const today = new Date();
      if (today > expiryDate) {
        // Message has expired, remove it
        localStorage.removeItem('team_message');
        setIsVisible(false);
        return;
      }
    }

    // For emergency messages, always show
    if (teamMessage.isEmergency) {
      setMessage(teamMessage);
      setIsVisible(true);
      return;
    }

    // For regular messages, check if already shown this session
    const sessionKey = `team-message-shown-${teamMessage.id}`;
    const wasShown = sessionStorage.getItem(sessionKey) === 'true';

    if (!wasShown) {
      setMessage(teamMessage);
      setIsVisible(true);
    }
  };

  const handleDismiss = () => {
    if (message && !message.isEmergency) {
      // Mark regular message as shown for this session
      const sessionKey = `team-message-shown-${message.id}`;
      sessionStorage.setItem(sessionKey, 'true');
    }
    
    setIsVisible(false);
  };

  const renderFormattedContent = (content) => {
    if (!content) return '';
    
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<u>$1</u>')
      .replace(/\n/g, '<br>');
  };

  if (!isVisible || !message) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          message.isEmergency 
            ? 'bg-red-50 border-red-200' 
            : 'bg-blue-50 border-blue-200'
        } rounded-t-xl`}>
          <div className="flex items-center space-x-2">
            {message.isEmergency && (
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <h3 className={`text-lg font-semibold ${
              message.isEmergency ? 'text-red-900' : 'text-blue-900'
            }`}>
              {message.isEmergency ? 'Emergency Notice' : 'Team Announcement'}
            </h3>
          </div>
          <button
            onClick={handleDismiss}
            className={`p-1 rounded-full hover:bg-opacity-20 transition-colors ${
              message.isEmergency 
                ? 'text-red-600 hover:bg-red-600' 
                : 'text-blue-600 hover:bg-blue-600'
            }`}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {message.title && (
            <h4 className="text-xl font-bold text-gray-900 mb-4">{message.title}</h4>
          )}
          
          <div 
            className="prose max-w-none text-gray-700 mb-6"
            dangerouslySetInnerHTML={{ __html: renderFormattedContent(message.content) }}
          />

          {message.expiryDate && (
            <div className="text-sm text-gray-500 mb-4">
              This message expires on {new Date(message.expiryDate).toLocaleDateString()}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleDismiss}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                message.isEmergency
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {message.isEmergency ? 'Got It' : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
